/**
 * Validation for the partner-entered width/height on an embed.
 *
 * The dimensions control is a free-text input whose value is inlined into a `style`
 * attribute, so it cannot be trusted verbatim (`100%;"><img src=x onerror=…>`). But it
 * also cannot be narrowed to a handful of units: the live preview sizes itself from the
 * same value, so rejecting a legitimate length like `calc(100vw - 2rem)` would make the
 * copied embed disagree with what the partner just saw. Hence one validator, shared by
 * the generator (./buildEmbedCode), the config reducer (./buildEmbedConfig) and the
 * control (../panel/WidgetConfigPanel), that accepts the whole length grammar — and only
 * values a browser will actually apply, so a rejected one falls back to the documented
 * default instead of being silently dropped at paint time.
 *
 * Math functions are parsed against the real `<calc-sum>` grammar rather than a
 * character whitelist: `calc(foo)` and `calc(1px +)` are balanced and innocuous-looking
 * but the browser discards them, which would leave the embed with no size at all.
 */

/**
 * The four viewport sizes — default, small, large and dynamic — each take the same six
 * axes, including the writing-mode-aware `vb`/`vi`. Deriving the 24 rather than spelling
 * them out keeps the set complete; written by hand it kept losing a variant.
 */
const CSS_VIEWPORT_UNITS = ['', 's', 'l', 'd'].flatMap((size) =>
    ['h', 'w', 'min', 'max', 'b', 'i'].map((axis) => `${size}v${axis}`),
);

/** Every CSS length unit plus `%` — anything a partner might reasonably size an embed with. */
const CSS_LENGTH_UNITS = [
    '%',
    'px',
    'em',
    'rem',
    'ex',
    'ch',
    'cap',
    'ic',
    'lh',
    // Each font-relative unit has a root-relative counterpart, resolved against the root
    // element's font rather than the embed's own.
    'rex',
    'rch',
    'rcap',
    'ric',
    'rlh',
    ...CSS_VIEWPORT_UNITS,
    'cqw',
    'cqh',
    'cqi',
    'cqb',
    'cqmin',
    'cqmax',
    'cm',
    'mm',
    'q',
    'in',
    'pt',
    'pc',
];

/**
 * Sizing keywords, which carry no unit. `stretch` fills the containing block; it is the
 * standard spelling of what used to need `-webkit-fill-available`, and is shipping.
 *
 * CSS Sizing 4 also defines `contain`, deliberately left out: no browser applies it yet, so
 * accepting it would hand the partner an embed with no size — the failure this validator
 * exists to prevent. It belongs here once it ships, not before.
 */
const CSS_SIZE_KEYWORDS = ['auto', 'min-content', 'max-content', 'fit-content', 'stretch'];

/**
 * The CSS-wide keywords, valid on every property. They name a cascade behaviour rather than
 * a size, but the browser applies them, which is the line this validator draws — and
 * `initial`/`unset` in particular mean `auto`, so rejecting them substituted a default that
 * sized the embed differently from what was asked for.
 */
const CSS_WIDE_KEYWORDS = ['inherit', 'initial', 'unset', 'revert', 'revert-layer'];

/**
 * The exponent is part of the numeric token, so `1e3px` is a thousand pixels to a browser.
 * It backtracks harmlessly on the units that start with `e`: in `2em` the `e` cannot begin
 * an exponent because no digits follow it, leaving `2` as the number and `em` as the unit.
 */
const CSS_EXPONENT = String.raw`(?:[eE][+-]?\d+)?`;
const CSS_NUMBER = String.raw`[+-]?(?:\d+(?:\.\d+)?|\.\d+)${CSS_EXPONENT}`;
/**
 * A width or height cannot be negative — the browser discards `-400px` outright — so the
 * plain-length form takes no sign but `+`. Inside a math function the sign stays legal:
 * `calc(100% - 400px)` is the whole point, and a negative *result* is clamped to 0 by the
 * browser rather than dropped, which no static check could tell apart anyway.
 */
const CSS_UNSIGNED_NUMBER = String.raw`\+?(?:\d+(?:\.\d+)?|\.\d+)${CSS_EXPONENT}`;
const CSS_LENGTH = new RegExp(`^${CSS_UNSIGNED_NUMBER}(?:${CSS_LENGTH_UNITS.join('|')})$`, 'i');
/**
 * A zero length may omit its unit, and the exception is about the *value*, not the
 * spelling: `0.0`, `+0` and `0e3` are all the same collapsed dimension to a browser. The
 * sign is allowed here where it is not on a real length, because `-0` is not a negative
 * size — it is zero, which the property accepts.
 */
const CSS_UNITLESS = new RegExp(`^${CSS_NUMBER}$`);

/** Long enough for a nested `clamp()`, short enough that no one can pad an attribute with it. */
const MAX_LENGTH = 100;

/**
 * Return the dimension unchanged when it is a CSS size we can safely inline into a
 * `style` attribute and a browser will honour, or `undefined` when it is not (so the
 * caller can fall back to its own default). Surrounding whitespace is trimmed rather
 * than treated as invalid.
 */
export function parseDimension(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    if (!trimmed || trimmed.length > MAX_LENGTH) return undefined;
    const keyword = trimmed.toLowerCase();
    if (CSS_SIZE_KEYWORDS.includes(keyword) || CSS_WIDE_KEYWORDS.includes(keyword)) return trimmed;
    if (isLengthPercentage(trimmed)) return trimmed;
    // `fit-content(<length-percentage>)` caps an intrinsic size. Its argument is a length,
    // not a size, so it takes none of the keywords above — hence not a recursive call.
    const fitContent = FIT_CONTENT.exec(trimmed);
    if (fitContent && isLengthPercentage(fitContent[1]!.trim())) return trimmed;
    return undefined;
}

const FIT_CONTENT = /^fit-content\((.*)\)$/i;

/** `<length-percentage>`: a plain length or percentage, or math that resolves to one. */
function isLengthPercentage(value: string): boolean {
    return CSS_LENGTH.test(value) || isUnitlessZero(value) || isMathFunction(value);
}

/**
 * A unitless number is only a length when it is zero — `400` on its own is discarded — so
 * the value is what decides, not the digits. Note this is a top-level rule only: inside
 * `calc()` an unadorned `0` stays a number, which is why `calc(0 + 1px)` is still invalid.
 */
function isUnitlessZero(value: string): boolean {
    return CSS_UNITLESS.test(value) && Number(value) === 0;
}

// --- <calc-sum> grammar (CSS Values and Units 4, §10) -----------------------
//
// The tokenizer only ever emits numbers, length units, arithmetic operators and the four
// math-function names, so nothing that could break out of an attribute survives it — the
// grammar is both the correctness check and the injection guard.

/**
 * What an expression resolves to. Lengths and percentages collapse into one type because
 * both are valid for width/height and `calc(100% - 2rem)` mixes them legally; a bare
 * number is tracked separately so `calc(1px + 1)` is caught.
 */
type MathType = 'number' | 'length';

interface Token {
    value: string;
    /** `+` and `-` are only operators when surrounded by whitespace. */
    spaceBefore: boolean;
}

interface Cursor {
    tokens: Token[];
    index: number;
}

const MATH_FUNCTIONS = new Set(['calc(', 'min(', 'max(', 'clamp(']);

/**
 * CSS whitespace, which is narrower than the regex `\s`: the CSS tokenizer treats NBSP and
 * the other Unicode spaces as ident code points rather than separators, so a dimension
 * pasted with one around its operator is a parse error the browser drops. Accepting it here
 * would show the partner no warning and hand them an embed with no size at all.
 */
const CSS_WHITESPACE = '[ \\t\\n\\r\\f]';
// Units are compared case-insensitively in numericType, so no `i` flag is needed here.
const TOKEN = new RegExp(
    `^(?:(${CSS_WHITESPACE}+)|([A-Za-z][A-Za-z0-9-]*\\()|(${CSS_NUMBER}(?:[A-Za-z]+|%)?)|([*/+\\-,()]))`,
);
const NUMERIC = new RegExp(`^(${CSS_NUMBER})(.*)$`);

function isMathFunction(value: string): boolean {
    const tokens = tokenize(value);
    if (!tokens || tokens.length === 0) return false;
    if (!MATH_FUNCTIONS.has(tokens[0]!.value.toLowerCase())) return false;
    const cursor: Cursor = { tokens, index: 0 };
    // A dimension must resolve to a length: `width: calc(2)` is discarded by the browser.
    return parseValue(cursor) === 'length' && cursor.index === tokens.length;
}

function tokenize(value: string): Token[] | undefined {
    const tokens: Token[] = [];
    let rest = value;
    let spaceBefore = false;

    while (rest.length > 0) {
        const match = TOKEN.exec(rest);
        if (!match) return undefined;
        const [matched, whitespace, fn, number, operator] = match;
        rest = rest.slice(matched.length);
        if (whitespace) {
            spaceBefore = true;
            continue;
        }
        tokens.push({ value: (fn ?? number ?? operator)!, spaceBefore });
        spaceBefore = false;
    }

    return tokens;
}

function peek(cursor: Cursor): Token | undefined {
    return cursor.tokens[cursor.index];
}

function take(cursor: Cursor): Token | undefined {
    return cursor.tokens[cursor.index++];
}

/** `<calc-sum> = <calc-product> [ ['+' | '-'] <calc-product> ]*` */
function parseSum(cursor: Cursor): MathType | undefined {
    const left = parseProduct(cursor);
    if (!left) return undefined;

    let type = left;
    for (;;) {
        const operator = peek(cursor);
        if (!operator || (operator.value !== '+' && operator.value !== '-')) return type;
        // Whitespace is required on both sides, so `calc(100%-2rem)` is not a subtraction.
        if (!operator.spaceBefore || !cursor.tokens[cursor.index + 1]?.spaceBefore) return undefined;
        cursor.index += 1;
        const right = parseProduct(cursor);
        // Adding a bare number to a length is invalid.
        if (!right || right !== type) return undefined;
    }
}

/** `<calc-product> = <calc-value> [ '*' <calc-value> | '/' <calc-number> ]*` */
function parseProduct(cursor: Cursor): MathType | undefined {
    let type = parseValue(cursor);
    if (!type) return undefined;

    for (;;) {
        const operator = peek(cursor);
        if (operator?.value === '*') {
            cursor.index += 1;
            const right = parseValue(cursor);
            // One operand must be a plain number: `calc(2px * 3px)` has no meaning.
            if (!right || (type === 'length' && right === 'length')) return undefined;
            if (type === 'number') type = right;
        } else if (operator?.value === '/') {
            cursor.index += 1;
            // Only division by a number is defined.
            if (parseValue(cursor) !== 'number') return undefined;
        } else {
            return type;
        }
    }
}

/** `<calc-value> = <number> | <dimension> | <percentage> | '(' <calc-sum> ')' | <math-function>` */
function parseValue(cursor: Cursor): MathType | undefined {
    const token = take(cursor);
    if (!token) return undefined;

    const name = token.value.toLowerCase();
    if (MATH_FUNCTIONS.has(name)) {
        const args: MathType[] = [];
        for (;;) {
            const argument = parseSum(cursor);
            if (!argument) return undefined;
            args.push(argument);
            if (peek(cursor)?.value !== ',') break;
            cursor.index += 1;
        }
        if (take(cursor)?.value !== ')') return undefined;
        if (name === 'calc(' && args.length !== 1) return undefined;
        if (name === 'clamp(' && args.length !== 3) return undefined;
        // Every argument has to be comparable to the others.
        if (args.some((argument) => argument !== args[0])) return undefined;
        return args[0];
    }

    if (token.value === '(') {
        const inner = parseSum(cursor);
        if (!inner || take(cursor)?.value !== ')') return undefined;
        return inner;
    }

    return numericType(token.value);
}

function numericType(token: string): MathType | undefined {
    const match = NUMERIC.exec(token);
    if (!match) return undefined;
    const unit = match[2]!;
    if (!unit) return 'number';
    return CSS_LENGTH_UNITS.includes(unit.toLowerCase()) ? 'length' : undefined;
}
